import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef, type ReactNode } from 'react'
import BottomNav from './BottomNav'
import SkipLink from './SkipLink'
import OfflineIndicator from './OfflineIndicator'
import { useAuth } from '../../context/AuthContext'
import { useAdminDashboardStats } from '../../hooks/queries/useSessions'

/** Props for AppShell component */
interface AppShellProps {
  showNav?: boolean
}

/** Props for Header component */
interface HeaderProps {
  title: string
  subtitle?: string
  rightContent?: ReactNode
  backButton?: {
    onClick: () => void
  }
  /** Show company logo next to title */
  showLogo?: boolean
}

/** Props for PageContainer component */
interface PageContainerProps {
  children?: ReactNode
  className?: string
  /** If true, content fills available space without extra scroll */
  tight?: boolean
}

function AppShell({ showNav = true }: AppShellProps): JSX.Element {
  const { userData } = useAuth()
  const isAdmin = userData?.role === 'admin'
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  // Only fetch admin stats for admin users to avoid unnecessary queries
  const { data: stats } = useAdminDashboardStats({ enabled: isAdmin })
  const pendingCount = isAdmin ? (stats?.pendingApprovals ?? 0) : 0

  // Reset scroll position and kill momentum when route changes
  useEffect(() => {
    if (mainRef.current) {
      // Use scrollTo with instant behavior to stop momentum scrolling
      mainRef.current.scrollTo({ top: 0, behavior: 'instant' })
      // Also briefly disable scroll to fully kill momentum
      const el = mainRef.current
      el.style.overflow = 'hidden'
      requestAnimationFrame(() => {
        el.style.overflow = ''
      })
    }
  }, [location.pathname])

  return (
    <div className="h-[100dvh] bg-gray-50 dark:bg-gray-900 flex flex-col pt-[env(safe-area-inset-top)]">
      {/* Skip link for accessibility */}
      <SkipLink />

      {/* Offline indicator */}
      <OfflineIndicator />

      {/* Main content area - uses flex to fill exactly available space */}
      <main ref={mainRef} id="main-content" role="main" className={`flex-1 min-h-0 overflow-y-auto overscroll-none ${isAdmin ? 'lg:pl-0' : ''}`}>
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
        {/* Spacer for fixed bottom nav */}
        {showNav && <div className="h-20 shrink-0" />}
      </main>

      {/* Bottom navigation (mobile) */}
      {showNav && <BottomNav isAdmin={isAdmin} pendingCount={pendingCount} />}
    </div>
  )
}

function Header({ title, subtitle, rightContent, backButton, showLogo = false }: HeaderProps): JSX.Element {
  return (
    <header
      className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-4 py-4 sticky top-0 z-40"
      role="banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {backButton && (
            <button
              onClick={backButton.onClick}
              className="p-2 -ml-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              aria-label="Go back"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {showLogo && !backButton && (
            <img
              src="/logo-icon.png?v=2"
              alt="Petru Drylining"
              className="w-10 h-10 rounded-lg shadow-sm"
            />
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
        {rightContent && (
          <div>{rightContent}</div>
        )}
      </div>
    </header>
  )
}

function PageContainer({ children, className = '', tight = false }: PageContainerProps): JSX.Element {
  if (tight) {
    // Tight layout: fills available space, no unnecessary scroll
    return (
      <div className={`px-4 py-4 flex flex-col flex-1 overflow-hidden ${className}`}>
        {children}
      </div>
    )
  }

  return (
    <div className={`px-4 py-4 ${className}`}>
      {children}
    </div>
  )
}

AppShell.Header = Header
AppShell.PageContainer = PageContainer

export default AppShell
